import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { defineWarmThemes } from '../components/EditorPanel';
import '../embed.css';

/*
 * EmbedPage — the chrome-less, anonymous, embeddable code surface (brookebits
 * B4a). Mounted directly from index.js when the path is /embed/<token>, so the
 * full app shell (auth, nav, theme system) never loads.
 *
 * Python runs ENTIRELY in the visitor's browser via Pyodide (CPython on WASM):
 * no login, no server run, no load on the API. Java can't do that (no WASM
 * JVM), so Java embeds POST to /api/embed/<token>/run instead (B4b) — a
 * single non-interactive run (no stdin) on the shared runner, gated server-
 * side by a per-IP/per-token rate limit and a cross-fleet concurrency cap
 * (app/concurrency_guard.py) since this is the one anonymous path to the
 * runner. See BROOKEBITS_PLAN.md § Embed.
 *
 * The bit is fetched from GET /api/embed/<token> (also unauthenticated).
 */

// Pinned Pyodide build (loaded lazily from CDN on first Run, not at page load).
// Bump deliberately; the indexURL must match the version.
const PYODIDE_VERSION = '0.26.4';
const PYODIDE_BASE = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

const API_URL = process.env.REACT_APP_API_URL || '';

// Replace input() with a synchronous bridge to window.prompt so the Python
// prompt text shows in the dialog and echoes to the terminal in the right
// order (Pyodide buffers a prompt written to stdout, which reorders output).
// A cancelled prompt returns None -> EOFError, matching real Python.
const INPUT_PREAMBLE = `
import builtins as _b
def input(prompt=""):
    _v = __embed_input(str(prompt))
    if _v is None:
        raise EOFError("EOF when reading a line")
    return _v
_b.input = input
del input
`;

/* Brand jigsaw mark (matches the /bits logo). fill=currentColor. */
function JigsawIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z" />
    </svg>
  );
}

/* Pop-out / open-elsewhere mark for the header's "open standalone" link. */
function PopoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </svg>
  );
}

/* Load the Pyodide script once and boot a single interpreter, memoised so a
   second Run reuses it. Returns a promise resolving to the pyodide instance. */
let _pyodidePromise = null;
function getPyodide() {
  if (_pyodidePromise) return _pyodidePromise;
  _pyodidePromise = new Promise((resolve, reject) => {
    if (window.loadPyodide) {
      window.loadPyodide({ indexURL: PYODIDE_BASE }).then(resolve, reject);
      return;
    }
    const script = document.createElement('script');
    script.src = `${PYODIDE_BASE}pyodide.js`;
    script.onload = () => {
      window.loadPyodide({ indexURL: PYODIDE_BASE }).then(resolve, reject);
    };
    script.onerror = () => reject(new Error('Could not load the Python engine.'));
    document.head.appendChild(script);
  });
  return _pyodidePromise;
}

function detectTheme() {
  // ?theme=dark|light lets the host page (InThinking) match its own scheme;
  // otherwise follow the visitor's OS preference.
  const q = new URLSearchParams(window.location.search).get('theme');
  if (q === 'dark' || q === 'light') return q;
  const prefersDark = window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

export default function EmbedPage() {
  const token = window.location.pathname.replace(/\/+$/, '').split('/embed/')[1] || '';

  const [loadState, setLoadState] = useState('loading'); // loading | ready | notfound | error
  const [bit, setBit]     = useState(null);              // { title, language, code }
  const [code, setCode]   = useState('');
  const [lines, setLines] = useState([]);                // [{ text, kind }]
  const [running, setRunning] = useState(false);
  const [booting, setBooting] = useState(false);         // first-run Pyodide download

  const theme = useRef(detectTheme()).current;
  const monacoTheme = theme === 'dark' ? 'ib-warm-dark' : 'ib-warm-light';
  const outRef = useRef(null);

  useEffect(() => { document.documentElement.setAttribute('data-embed-theme', theme); }, [theme]);

  // Fetch the embed payload (no auth).
  useEffect(() => {
    let cancelled = false;
    if (!token) { setLoadState('notfound'); return; }
    fetch(`${API_URL}/api/embed/${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.status === 404) { if (!cancelled) setLoadState('notfound'); return null; }
        if (!r.ok) { if (!cancelled) setLoadState('error'); return null; }
        return r.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        setBit(data);
        setCode(data.code || '');
        setLoadState('ready');
      })
      .catch(() => { if (!cancelled) setLoadState('error'); });
    return () => { cancelled = true; };
  }, [token]);

  // Autoscroll the output as it grows.
  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [lines]);

  const append = useCallback((text, kind = 'out') => {
    if (text === '' || text == null) return;
    setLines((prev) => [...prev, { text, kind }]);
  }, []);

  const handleBeforeMount = useCallback((monaco) => { defineWarmThemes(monaco); }, []);

  const runJava = useCallback(async () => {
    setRunning(true);
    setLines([]);
    try {
      const res = await fetch(`${API_URL}/api/embed/${encodeURIComponent(token)}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
        append('Too many runs right now — please wait a moment and try again.', 'err');
        return;
      }
      if (res.status === 503) {
        append(body.error || 'The execution service is busy. Please try again shortly.', 'err');
        return;
      }
      if (!res.ok) {
        append(body.error || 'Something went wrong running this code.', 'err');
        return;
      }
      if (body.output) append(body.output, 'out');
      if (body.errors) append(body.errors, 'err');
      if (!body.output && !body.errors) append('(no output)', 'out');
    } catch (e) {
      append('Could not reach the execution service. Check your connection and try again.', 'err');
    } finally {
      setRunning(false);
    }
  }, [code, token, append]);

  const run = useCallback(async () => {
    if (running || !bit) return;
    if (bit.language === 'java') { runJava(); return; }
    if (bit.language !== 'python') return;
    setRunning(true);
    setLines([]);
    let py;
    try {
      if (!window.__pyodideReady) setBooting(true);
      py = await getPyodide();
      window.__pyodideReady = true;
    } catch (e) {
      setBooting(false);
      setRunning(false);
      append('Could not load the Python engine. Check your connection and try again.', 'err');
      return;
    }
    setBooting(false);

    // Pyodide's batched stdout/stderr is called once per line WITHOUT the
    // trailing newline, so re-add it to preserve line breaks.
    py.setStdout({ batched: (s) => append(s + '\n', 'out') });
    py.setStderr({ batched: (s) => append(s + '\n', 'err') });
    // input() bridge (see INPUT_PREAMBLE): show the prompt, then the typed value.
    py.globals.set('__embed_input', (prompt) => {
      const p = String(prompt == null ? '' : prompt);
      if (p) append(p, 'out');                       // prompt, in order
      let v;
      try {
        v = window.prompt(p || 'Program input:');    // sync; blocks the run
      } catch (e) {
        // Some sandboxed iframes disallow modals (no allow-modals). Fail soft.
        append('\n[keyboard input is not available in this embed]\n', 'err');
        return null;
      }
      if (v === null) return null;                   // Cancel -> EOFError
      append(v + '\n', 'in');                        // echo the entered value
      return v;
    });

    try {
      await py.runPythonAsync(INPUT_PREAMBLE);
      await py.runPythonAsync(code);
    } catch (e) {
      // PythonError.message carries the formatted traceback.
      append(String(e && e.message ? e.message : e), 'err');
    } finally {
      setRunning(false);
    }
  }, [running, bit, code, append, runJava]);

  const reset = useCallback(() => {
    if (!bit) return;
    setCode(bit.code || '');
    setLines([]);
  }, [bit]);

  // ── Render states ──────────────────────────────────────────────────────
  if (loadState === 'loading') {
    return <div className="embed-root embed-centered"><div className="embed-muted">Loading…</div></div>;
  }
  if (loadState === 'notfound') {
    return (
      <div className="embed-root embed-centered">
        <div className="embed-message">
          <JigsawIcon className="embed-message-logo" />
          <p>This embed isn’t available.</p>
          <a href="https://brookebit.com" target="_blank" rel="noreferrer">brookebit.com</a>
        </div>
      </div>
    );
  }
  if (loadState === 'error') {
    return (
      <div className="embed-root embed-centered">
        <div className="embed-message"><p>Something went wrong loading this embed.</p></div>
      </div>
    );
  }

  const isPython = bit.language === 'python';
  const isJava   = bit.language === 'java';

  return (
    <div className="embed-root">
      <div className="embed-topbar" />
      <div className="embed-header">
        <a className="embed-header-brand" href="https://brookebit.com" target="_blank" rel="noreferrer" title="Made with brookebit">
          <JigsawIcon className="embed-header-logo" />
          <span>brookebit</span>
        </a>
        <span className="embed-header-divider" />
        <span className="embed-header-title" title={bit.title}>{bit.title || 'Untitled'}</span>
        <span className={`embed-header-lang embed-header-lang-${bit.language}`}>
          {isJava ? 'Java' : 'Python'}
        </span>
        <span className="embed-header-spacer" />
        <a className="embed-header-popout" href={window.location.href} target="_blank" rel="noreferrer" title="Open in a new tab">
          <PopoutIcon />
        </a>
      </div>
      <div className="embed-editor-wrap">
        <Editor
          height="100%"
          language={bit.language}
          value={code}
          onChange={(v) => setCode(v ?? '')}
          theme={monacoTheme}
          beforeMount={handleBeforeMount}
          options={{
            fontFamily: '"JetBrains Mono", "Cascadia Code", "Fira Code", Consolas, "Courier New", monospace',
            fontLigatures: true,
            fontSize: 13,
            lineHeight: Math.round(1.6 * 13),
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'off',
            automaticLayout: true,
            padding: { top: 10, bottom: 10 },
            smoothScrolling: true,
          }}
        />
      </div>

      <div className="embed-actions">
        <button
          className="embed-run"
          onClick={run}
          disabled={running}
          title="Run this code"
        >
          {running ? (isPython && booting ? 'Loading Python…' : 'Running…') : '► Run'}
        </button>
        <button className="embed-reset" onClick={reset} disabled={running} title="Restore the original code">
          Reset
        </button>
      </div>

      {isJava && (
        <div className="embed-note">
          This Java example runs once per click and can't take typed input — a Scanner
          reading input will see end-of-input.
        </div>
      )}

      <pre className="embed-output" ref={outRef} aria-live="polite">
        {lines.length === 0
          ? <span className="embed-muted">Output will appear here after you press Run.</span>
          : lines.map((l, i) => <span key={i} className={`embed-line embed-line-${l.kind}`}>{l.text}</span>)}
      </pre>
    </div>
  );
}
